/**
 * Select Site Header JavaScript
 *
 * For header.php (Select, blog 2) only — binds the markup in
 * templates/new-nav.php. Enqueued from functions.php as
 * `pacific-header-select` (Vite entry → dist/js/header-select.js).
 *
 * Ported 2026-09-15 from the legacy `SiteFunctions` class
 * (legacy repo whoiskjl/2026-pacific-gutenberg-theme-new,
 * custom-assets/js/custom.js → build/js/custom.min.js) so the legacy
 * bundle could be retired. Behaviour is kept 1:1; only code paths that
 * were never wired up in legacy (sign-in placeholder form, .go-back) are
 * dropped, and the scroll handler that closed the desktop menu on every
 * scroll event is not ported (select.js had been suppressing it since the
 * sticky header landed).
 *
 * Handles:
 * - Mobile burger open / close (.header__mobile-burger / .header__mobile-close)
 * - Desktop mega-menu: panels fetched from general-injection/v1/options and
 *   injected into .desktop-menu-wrapper; hovering a .dropdown-menu-item
 *   (desktop) or tapping it (mobile) reveals the matching [data-parentitem]
 *   panel via .child-level-visible / .sub-menu-visible
 *
 * Cooperates with assets/js/select.js, which owns the scroll lock on
 * body.element-blured, the mobile submenu drawer (.submenu-panel-drawer)
 * and the sign-in panel. select.js triggers .header__mobile-close.click()
 * from the drawer close button, so closeMobileMenu() must stay bound here.
 */
(function () {
    'use strict';

    var body = document.body;
    var mainElement = document.querySelector('#main');
    var burger = document.querySelector('.header__mobile-burger');
    var closeIcon = document.querySelector('.header__mobile-close');
    var positionWrapper = document.querySelector('.header__position-wrapper');
    var desktopWrapper = document.querySelector('.desktop-menu-wrapper');
    var navbar = document.querySelector('.header__navbar');

    // Not the Select header markup (e.g. a template without new-nav.php).
    if (!positionWrapper || !desktopWrapper) return;

    // Legacy breakpoint: > 991px is desktop, < 992px is mobile.
    function isDesktop() {
        return window.innerWidth > 991;
    }

    function headerHovered() {
        return !!document.querySelector('.header:hover');
    }

    // ==========================================================================
    // Mobile menu (burger / close)
    // ==========================================================================

    function openMobileMenu() {
        if (burger) burger.style.display = 'none';
        if (closeIcon) closeIcon.style.display = 'block';
        positionWrapper.classList.add('menu-visible');
        body.classList.add('element-blured');
    }

    function closeMobileMenu() {
        if (burger) burger.style.display = 'block';
        if (closeIcon) closeIcon.style.display = 'none';
        positionWrapper.classList.remove('menu-visible');
        desktopWrapper.classList.remove('menu-visible');
        document.querySelectorAll('.child-level-visible').forEach(function (el) {
            el.classList.remove('child-level-visible');
        });
        document.querySelectorAll('.sub-menu.sub-menu-visible').forEach(function (el) {
            el.classList.remove('sub-menu-visible');
        });
        body.classList.remove('element-blured');
        if (mainElement) mainElement.classList.remove('element-blured');
    }

    if (burger) burger.addEventListener('click', openMobileMenu);
    if (closeIcon) closeIcon.addEventListener('click', closeMobileMenu);

    // ==========================================================================
    // Desktop mega-menu: fetch + inject, then hover / tap behaviour
    // ==========================================================================

    // The panels come from the General site's REST route (Pacific_Routes
    // register_injection_menu). Easyling serves translations under /es/ and
    // /pt/, and fetching through that same prefix returns the panels
    // translated with localized links. The path is the language signal, not
    // <html lang>, whose value Easyling has changed before (es-ES → es-US).
    function menuEndpoint() {
        var langPrefix = window.location.pathname.match(/^\/(es|pt)\//);
        var basePath = langPrefix ? '/' + langPrefix[1] : '';
        return window.location.origin + basePath + '/wp-json/general-injection/v1/options';
    }

    function injectMenuItems() {
        fetch(menuEndpoint())
            .then(function (response) { return response.json(); })
            .then(function (data) {
                if (!Array.isArray(data)) return;
                var html = '';
                data.forEach(function (item) {
                    if (item && typeof item.html === 'string') html += item.html;
                });
                // Same-origin, theme-generated markup (Pacific_Routes builds it
                // from the WP menus) — the same trust boundary legacy relied on.
                desktopWrapper.insertAdjacentHTML('beforeend', html);
                bindInjectedMenu();
            })
            .catch(function (error) {
                console.error(error);
            });
    }

    // The [data-parentitem] panels a top-level item controls: one per
    // `childrens-*` class on the item.
    function panelsFor(menuItem) {
        var panels = [];
        for (var i = 0; i < menuItem.classList.length; i++) {
            var className = menuItem.classList[i];
            if (className.indexOf('childrens-') === 0) {
                var panel = document.querySelector('[data-parentitem="' + className + '"]');
                if (panel) panels.push(panel);
            }
        }
        return panels;
    }

    function hideAllPanels() {
        document.querySelectorAll('[data-parentitem]').forEach(function (el) {
            el.classList.remove('child-level-visible');
        });
    }

    function openDesktopMenu() {
        desktopWrapper.classList.add('menu-visible');
        if (navbar) navbar.classList.add('element-blured');
    }

    function closeDesktopMenu() {
        desktopWrapper.classList.remove('menu-visible');
        if (navbar) navbar.classList.remove('element-blured');
    }

    function bindInjectedMenu() {
        // Top-level items with dropdowns — present in both the desktop list
        // (.header__menu-list) and its mobile copy (.mobile-menu-wrapper).
        document.querySelectorAll('.dropdown-menu-item.menu-item').forEach(function (menuItem) {
            menuItem.addEventListener('mouseover', function () {
                if (!isDesktop()) return;
                openDesktopMenu();
                hideAllPanels();
                panelsFor(menuItem).forEach(function (panel) {
                    panel.classList.add('child-level-visible');
                });
            });

            menuItem.addEventListener('mouseleave', function () {
                if (!isDesktop()) return;
                if (!headerHovered()) closeDesktopMenu();
            });

            menuItem.addEventListener('click', function () {
                if (isDesktop()) return;
                desktopWrapper.classList.add('menu-visible');
                var openSubMenu = document.querySelector('.sub-menu.sub-menu-visible');
                if (openSubMenu) openSubMenu.classList.remove('sub-menu-visible');
                var subMenu = menuItem.querySelector('.sub-menu');
                panelsFor(menuItem).forEach(function (panel) {
                    if (panel.classList.contains('child-level-visible')) {
                        panel.classList.remove('child-level-visible');
                        if (subMenu) subMenu.classList.remove('sub-menu-visible');
                    } else {
                        hideAllPanels();
                        panel.classList.add('child-level-visible');
                        if (subMenu) subMenu.classList.add('sub-menu-visible');
                    }
                });
            });
        });

        desktopWrapper.addEventListener('mouseover', function () {
            if (isDesktop()) openDesktopMenu();
        });

        desktopWrapper.addEventListener('mouseleave', function () {
            if (!isDesktop()) return;
            if (document.querySelector('.header__navbar:hover') || !headerHovered()) {
                closeDesktopMenu();
            }
        });
    }

    injectMenuItems();
})();
